import React, { useEffect } from 'react';
import { installMobileTabs } from '../shared/utils/mobile-tabs';
import { installImageFallback } from '../lib/imageFallback';
import Head from 'next/head';
import Script from 'next/script';
import BaseLayout from '../components/Layout/Layout';
import { useProvideRemoteConfig, useUserData } from '../lib/hooks';
import '../styles/index.scss';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { store } from '../store';
import '../lib/i18n';
import { useRouter } from 'next/router';
import PushProvider from '../components/Notifications/PushProvider';
import { InstallAppProvider } from '../lib/installApp';

const queryClient = new QueryClient();

const GA_ID = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS;
// Cloudflare Web Analytics (gerçek kullanıcı performansı; çerezsiz, DNS/proxy gerektirmez).
const CF_BEACON_TOKEN = process.env.NEXT_PUBLIC_CF_WEB_ANALYTICS_TOKEN;
const isProduction = process.env.NODE_ENV === 'production';

const TopProgressBar = dynamic(() => import('../components/Layout/TopProgressBar'), { ssr: false });

function AppInitializer() {
  useUserData();
  useProvideRemoteConfig();
  return null;
}

export default function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();

  useEffect(() => installMobileTabs(), []);
  useEffect(() => installImageFallback(), []);

  useEffect(() => {
    if (!isProduction || !GA_ID) return undefined;

    const trackPageView = (url: string) => {
      (window as any).gtag?.('config', GA_ID, { page_path: url });
    };

    router.events.on('routeChangeComplete', trackPageView);
    return () => router.events.off('routeChangeComplete', trackPageView);
  }, [router.events]);

  return (
    <Provider store={store}>
      {/* GA hydration'dan sonra yüklenir; _document'taki eşzamansız script'e göre
          ana iş parçacığıyla (TBT) daha az yarışır */}
      {isProduction && GA_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
            strategy="afterInteractive"
          />
          <Script id="ga-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_ID}', { page_path: window.location.pathname });
            `}
          </Script>
        </>
      )}
      {/* Sayfa tamamen yüklendikten sonra; LCP/TBT ile yarışmaz. SPA geçişlerini beacon kendi izler. */}
      {isProduction && CF_BEACON_TOKEN && (
        <Script
          src="https://static.cloudflareinsights.com/beacon.min.js"
          strategy="lazyOnload"
          data-cf-beacon={JSON.stringify({ token: CF_BEACON_TOKEN, spa: true })}
        />
      )}
      <QueryClientProvider client={queryClient}>
        <AppInitializer />
        <PushProvider>
        <InstallAppProvider>
        <BaseLayout>
          <Head>
            <title>Tenis oyna, partner bul, kort seç! | tenis.io</title>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, viewport-fit=cover"
            />
          </Head>
          <TopProgressBar />
          <Component {...pageProps} />
        </BaseLayout>
        </InstallAppProvider>
        </PushProvider>
      </QueryClientProvider>
    </Provider>
  );
}
